import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { Popover } from 'primeng/popover';
import { InviteAcceptDialogComponent } from 'src/app/commons/components/invite-accept-dialog/invite-accept-dialog.component';
import { SHARED_MODULES, TranslatePipe } from '../../modules/shared-imports';
import { NotificationService } from '../notification.service';
import { AppNotification } from '../notification.model';
import { PushNotificationService } from '../../services/push-notification.service';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { LiveNumberComponent } from 'src/app/commons/components/live-number/live-number.component';

/**
 * Header bell — displays unreadCount$/refresh$ from NotificationService, fed by both the
 * real-time WebSocket push (NotificationSocketService) and its REST disconnected-fallback poll;
 * this component only does a one-shot initial fetch, it does not own the poll's lifecycle (see
 * docs/notification-build/PLAN.md). Standalone by design so the whole feature stays removable:
 * deleting this folder + the one <app-notification-bell> tag in header.component.html + the one
 * bell-related task in NotificationService reverts the UI entirely (PLAN.md §2.2).
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [InviteAcceptDialogComponent, SHARED_MODULES, TranslatePipe, LiveNumberComponent],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly logger = inject(NGXLogger);
  private readonly router = inject(Router);

  @ViewChild('op') overlayPanel?: Popover;

  unreadCount = 0;
  items: AppNotification[] = [];
  loading = false;
  actingOnId: number | null = null;
  ringing = false;
  /** Badge "pop" when the count goes up (2026-09-28). */
  badgePop = false;
  /** Increases seen in the first moments after the bell appears are the initial load, not news. */
  private settled = false;
  private settleTimeout?: ReturnType<typeof setTimeout>;
  private popTimeout?: ReturnType<typeof setTimeout>;

  private subscriptions: Subscription[] = [];
  private ringTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private notificationService: NotificationService,
    private pushNotificationService: PushNotificationService,
    private toastMessageService: ToastMessagesService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      // 2026-09-28: ring the bell + pop the badge whenever the unread count goes UP — from the live
      // connection, a push, or a catch-up after reconnect — not only on a foreground push. Never on
      // the first load of the page (settle window below), never when the count goes down (read).
      this.notificationService.unreadCount$.subscribe((count) => {
        if (this.settled && count > this.unreadCount) {
          this.ring();
          this.pop();
        }
        this.unreadCount = count;
      }),
      this.notificationService.refresh$.subscribe(() => this.loadList()),
      // Foreground push arrived — briefly animate the bell so it draws the eye the same way a
      // native OS banner would if the app weren't already open. Purely visual; the unread count
      // itself already updates independently via unreadCount$/refresh$ above (the WS channel
      // that drives those is separate from the push channel — see PushNotificationService's own
      // comment on why the two aren't coupled).
      this.pushNotificationService.foregroundPush.subscribe(() => this.ring()),
    );
    // One-shot initial paint only — NotificationSocketService owns the poll's start/stop
    // lifecycle (active only while the WebSocket is disconnected), decoupled from whether this
    // component happens to be mounted.
    this.notificationService.refreshUnreadCount();
    this.settleTimeout = setTimeout(() => (this.settled = true), 2500);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    clearTimeout(this.ringTimeout);
    clearTimeout(this.settleTimeout);
    clearTimeout(this.popTimeout);
  }

  /** Swing the bell once. Re-triggers even if it is already mid-ring: class off, then on next tick. */
  private ring(): void {
    clearTimeout(this.ringTimeout);
    this.ringing = false;
    setTimeout(() => {
      this.ringing = true;
      this.ringTimeout = setTimeout(() => (this.ringing = false), 1000);
    });
  }

  private pop(): void {
    clearTimeout(this.popTimeout);
    this.badgePop = false;
    setTimeout(() => {
      this.badgePop = true;
      this.popTimeout = setTimeout(() => (this.badgePop = false), 600);
    });
  }

  onToggle(event: Event): void {
    this.overlayPanel?.toggle(event);
    this.loadList();
  }

  loadList(): void {
    this.loading = true;
    this.notificationService.list(0, 20).subscribe({
      next: (res) => {
        this.items = res?.enabled ? (res.content ?? []) : [];
        this.loading = false;
      },
      error: () => {
        this.items = [];
        this.loading = false;
      }
    });
  }

  markAllRead(): void {
    this.notificationService.markAllRead().subscribe({
      error: (e) => this.logger.warn('[NotificationBell] markAllRead failed', e)
    });
  }

  onCardClick(item: AppNotification): void {
    if (item.status === 'UNREAD' && item.actionType !== 'ACCEPT_DECLINE_INVITE') {
      this.notificationService.markRead(item.id).subscribe();
    }
  }

  /** The accept button: first ask for the "I agree" tick (nobody joins a company without agreeing to the terms). */
  acceptItem: AppNotification | null = null;
  accept(item: AppNotification, event: Event): void {
    event.stopPropagation();
    this.acceptItem = item;
  }

  confirmAccept(): void {
    const item = this.acceptItem;
    if (!item) { return; }
    this.actingOnId = item.id;
    this.notificationService.acceptInvite(item.id, true).subscribe({
      next: () => { this.acceptItem = null; this.actingOnId = null; this.loadList(); },
      error: (e) => {
        this.acceptItem = null;
        this.actingOnId = null;
        this.logger.warn('[NotificationBell] accept failed', e);
        // Real gap found 2026-09-06: this used to only log to the console — the user saw
        // nothing at all (e.g. an expired invite's rejection was invisible).
        this.toastMessageService.showHttpErrorMessage(e, 'Could not accept this invitation.');
      }
    });
  }

  decline(item: AppNotification, event: Event): void {
    event.stopPropagation();
    this.actingOnId = item.id;
    this.notificationService.declineInvite(item.id).subscribe({
      next: () => { this.acceptItem = null; this.actingOnId = null; this.loadList(); },
      error: (e) => {
        this.actingOnId = null;
        this.logger.warn('[NotificationBell] decline failed', e);
        this.toastMessageService.showHttpErrorMessage(e, 'Could not decline this invitation.');
      }
    });
  }

  viewAll(): void {
    this.overlayPanel?.hide();
    this.router.navigate(['/notifications']);
  }

  badgeLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }
}
